/* AllRoundsPage.js */
import React, { useState, useEffect } from "react";
import { useRef } from "react";
import styles from "./AllRoundsPage.module.css";
import teamLogos from "../utils/teamLogos";
import headToHead from "./images/solid-head-to-head-24.png";
import placeholderLogo from "./images/logo-placeholder-22.png";
import { getCurrentRound } from "../utils/roundUtils";

// helper to turn round number into a label
const roundLabel = (r) =>
  r === 0
    ? "OR"
    : r === 25
    ? "QE"
    : r === 26
    ? "SF"
    : r === 27
    ? "PF"
    : r === 28
    ? "GF"
    : String(r);

// build the list of all rounds
const roundLabels = [
  { id: 0 },
  ...Array.from({ length: 24 }, (_, i) => ({ id: i + 1 })),
  { id: 25 },
  { id: 26 },
  { id: 27 },
  { id: 28 },
];

const getTeamDisplayName = (team) => {
  const teamNamesMap = {
    Adelaide: "Adelaide",
    "Brisbane Lions": "Brisbane",
    Carlton: "Carlton",
    Collingwood: "Collingwood",
    Essendon: "Essendon",
    Fremantle: "Fremantle",
    Geelong: "Geelong",
    "Gold Coast": "Gold Coast",
    "Greater Western Sydney": "Giants",
    Hawthorn: "Hawthorn",
    Melbourne: "Melbourne",
    "North Melbourne": "North Melb",
    "Port Adelaide": "Port Adelaide",
    Richmond: "Richmond",
    "St Kilda": "Saint Kilda",
    Sydney: "Sydney",
    "West Coast": "West Coast",
    "Western Bulldogs": "Bulldogs",
  };
  return teamNamesMap[team] || team;
};

const AllRoundsPage = () => {
  const currentYear = new Date().getFullYear();
  const nowEpoch = Math.floor(Date.now() / 1000);
  const initialRound = getCurrentRound(nowEpoch);

  const [matches, setMatches] = useState([]);
  const [selectedRound, setSelectedRound] = useState(initialRound);

  const roundsBarRef = useRef(null);

  useEffect(() => {
    // fetch fixtures for today's round on mount
    handleRoundSelection(initialRound);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!roundsBarRef.current) return;
    const activeEl = roundsBarRef.current.querySelector(
      `.${CSS.escape(styles.active)}`
    );
    if (activeEl && activeEl.scrollIntoView) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedRound]);

  const handleRoundSelection = async (roundNumber) => {
    setSelectedRound(roundNumber);
    const url = `https://api.squiggle.com.au/?q=games;year=${currentYear};round=${roundNumber}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      setMatches(data.games || []);
    } catch (error) {
      console.error("Error fetching match data:", error);
      setMatches([]);
    }
  };

  const getDayAndFormattedDate = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
    });
    return { dayName, formattedDate };
  };

  const formatMatchTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const groupedMatches = matches.reduce((acc, match) => {
    const { dayName, formattedDate } = getDayAndFormattedDate(match.date);
    if (!acc[dayName]) acc[dayName] = { formattedDate, matches: [] };
    acc[dayName].matches.push(match);
    return acc;
  }, {});

  return (
    <div className={styles.allRoundsPage}>
      <h1 className={styles.allRoundsTitle}>{currentYear} AFL Season</h1>

      {/* ← side-scrolling round selector */}
      <div className={styles.roundsBar} ref={roundsBarRef}>
        {roundLabels.map(({ id }) => {
          const isCurrent = id === initialRound;
          const isSelected = id === selectedRound;
          const isCompleted = id < initialRound;

          return (
            <div
              key={id}
              onClick={() => handleRoundSelection(id)}
              className={[
                styles.roundCircle,
                isSelected && styles.active,
                !isSelected && isCurrent && styles.current,
                !isSelected && isCompleted && styles.completedRound,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {roundLabel(id)}
            </div>
          );
        })}
      </div>

      <div className={styles.matchResultsContainer}>
        {Object.keys(groupedMatches).map((dayName, idx) => (
          <div key={idx} className={styles.matchDayGroup}>
            <div className={styles.matchDayHeader}>
              <span>{dayName}</span>
              <span className={styles.matchDate}>
                {groupedMatches[dayName].formattedDate}
              </span>
            </div>
            {groupedMatches[dayName].matches.map((m, mi) => (
              <div key={mi} className={styles.matchCard}>
                <div className={styles.teamNamesRow}>
                  <span className={styles.teamNameHome}>
                    {getTeamDisplayName(m.hteam)}
                  </span>
                  <span className={styles.teamNameAway}>
                    {getTeamDisplayName(m.ateam)}
                  </span>
                </div>
                <div className={styles.matchDetails}>
                  <div className={styles.matchTime}>
                    {formatMatchTime(m.date)}
                  </div>
                  <div className={styles.teamHome}>
                    <img
                      src={teamLogos[m.hteam] || placeholderLogo}
                      alt={`${m.hteam} logo`}
                      className={styles.teamLogoHome}
                    />
                    <span className={styles.teamScoreHome}>{m.hscore}</span>
                  </div>
                  <div className={styles.vs}>
                    <img src={headToHead} alt="Head to Head" />
                  </div>
                  <div className={styles.teamAway}>
                    <span className={styles.teamScoreAway}>{m.ascore}</span>
                    <img
                      src={teamLogos[m.ateam] || placeholderLogo}
                      alt={`${m.ateam} logo`}
                      className={styles.teamLogoAway}
                    />
                  </div>
                  <div className={styles.matchStatus}>{m.timestr}</div>
                </div>
                <div className={styles.venueRow}>
                  <span className={styles.venue}>{m.venue}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllRoundsPage;
